// Primera generación del onboarding: de la foto que sube el usuario a las imágenes
// generadas. Extraído tal cual de `pages/AddFirstProduct.tsx`
// (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real), donde vivía mezclado con la UI
// de esa pantalla y por eso no se podía reusar desde el cuestionario nuevo.
//
// Son dos pasos separados a propósito, porque la UI los muestra en momentos distintos:
//   1. `prepararImagen(file)`      — al elegir el archivo: valida, convierte y redimensiona.
//   2. `generarPrimeraImagen(...)` — al tocar "Generar": crea producto + variante, sube
//      la imagen, categoriza y dispara la generación real.
//
// Nada de esto aplica al camino de los productos de EJEMPLO del paso 4: ésos muestran
// resultados pre-armados, no gastan créditos y no pasan por acá.

import { supabase } from './supabase';
import { ensureDefaultBrand } from './ensureDefaultBrand';
import {
  normalizeImageForUpload,
  UnsupportedImageFormatError,
  UNSUPPORTED_FORMAT_MESSAGE,
} from '../utils/heicConvert';
import { categorizeProduct } from './categorizeProduct';
import { postGenerateAd, postGenerateAdAtlas, GenerateAdError } from './generateAdRequest';
import { awaitImageGeneration } from '../pages/v2/imageGenerationRecovery';
import { pollGeneratedByExecutionId } from '../pages/v2/pollGeneratedByExecutionId';
import { USE_ATLAS_BACKEND } from '../config';
import { atlasBackendFetch } from './atlasBackend';
import { resizeImageIfNeeded } from '../utils/imageResize';
import { buildThumbnailBlob } from '../utils/thumbnail';

/** Tope de subida (100 MB), igual que en el resto de la app. */
const MAX_FILE_SIZE = 100 * 1024 * 1024;
/** Lado máximo al que se reduce la imagen antes de subirla. */
const MAX_LADO_PX = 2000;
/** Cuántas imágenes genera el onboarding. También es lo que espera el poll. */
export const CANTIDAD_IMAGENES = 2;

/** Error con mensaje ya listo para mostrarle al usuario. */
export class ImagenInvalidaError extends Error {}

/**
 * Traspaso de la foto entre el paso 4 y la pantalla de "generando".
 *
 * Un `File` no entra en localStorage y no queremos meterlo en el state del router, así
 * que viaja por memoria: el paso 4 la deja acá al navegar y la pantalla de generación
 * arranca desde acá.
 *
 * **La promesa se cachea, y esto no es un detalle.** Una primera versión "tomaba" la
 * foto consumiéndola en la lectura, y con `StrictMode` (activo en dev) los efectos
 * corren dos veces: la segunda lectura no encontraba nada, la pantalla de generación
 * rebotaba al paso 4 y desde afuera se veía como que el botón "Generar" no hacía nada.
 * Cachear la promesa arregla eso y, sobre todo, garantiza **una sola generación**: dos
 * disparos serían dos consumos de créditos del usuario.
 */
let fotoPendiente: { file: File; nombre: string } | null = null;
let generacionEnCurso: Promise<PrimeraGeneracionResult> | null = null;

/**
 * Recursos creados por un intento que FALLÓ a mitad (producto/variante/fila de
 * imagen). El reintento los REUSA en vez de crear otros: sin esto, cada retry
 * dejaba un producto huérfano y duplicaba filas. Se limpia solo en éxito.
 */
let recursosPrevios: {
  /** Empresa dueña: si cambió la cuenta (logout/login sin recargar la SPA),
   *  estos recursos son de OTRA empresa y no deben reusarse. */
  idCompany: string;
  productId: string;
  /** null = el intento anterior murió ENTRE el insert del producto y el de la
   *  variante; el retry la completa sobre el mismo producto. */
  variantId: string | null;
  nombre: string;
  imagenInsertada: boolean;
} | null = null;

/**
 * Marcador PERSISTENTE (localStorage) de la generación en vuelo. El estado de módulo
 * muere con un refresh: sin este marcador, la pantalla de "generando" rebotaba al
 * paso 4 mientras el backend seguía generando, y el reintento del usuario creaba OTRO
 * producto y consumía créditos de nuevo (el primer resultado quedaba solo en el
 * historial). Con él, `recuperarGeneracionPendiente` re-engancha el poll.
 */
const PENDIENTE_KEY = 'atlas-onboarding-pending-generation';
const PENDIENTE_MAX_AGE_MS = 10 * 60 * 1000;

interface MarcaPendiente {
  executionId: string;
  companyId: string;
  variantId: string;
  startedAt: number;
}

function marcarPendiente(m: Omit<MarcaPendiente, 'startedAt'>): void {
  try {
    localStorage.setItem(PENDIENTE_KEY, JSON.stringify({ ...m, startedAt: Date.now() }));
  } catch {
    /* modo privado — non-fatal */
  }
}

function limpiarMarcaPendiente(): void {
  try {
    localStorage.removeItem(PENDIENTE_KEY);
  } catch {
    /* noop */
  }
}

function leerMarcaPendiente(): MarcaPendiente | null {
  try {
    const raw = localStorage.getItem(PENDIENTE_KEY);
    if (!raw) return null;
    const m = JSON.parse(raw) as MarcaPendiente;
    if (!m?.executionId || !m?.companyId || !m?.variantId) {
      limpiarMarcaPendiente();
      return null;
    }
    if (Date.now() - (m.startedAt || 0) > PENDIENTE_MAX_AGE_MS) {
      limpiarMarcaPendiente();
      return null;
    }
    return m;
  } catch {
    return null;
  }
}

/** El paso 4 deja la foto lista antes de navegar. Descarta cualquier intento previo. */
export function dejarFotoParaGenerar(file: File, nombre: string): void {
  fotoPendiente = { file, nombre };
  generacionEnCurso = null;
}

/**
 * Arranca la generación pendiente, o devuelve la que ya está corriendo. `null` si no
 * hay foto (refresh en la pantalla de generación o deep-link): ahí el caller manda al
 * usuario de vuelta al paso 4.
 *
 * Idempotente: llamarla N veces dispara UNA sola generación.
 */
export function generacionPendiente(): Promise<PrimeraGeneracionResult> | null {
  if (generacionEnCurso) return generacionEnCurso;
  if (!fotoPendiente) return null;
  const { file, nombre } = fotoPendiente;
  generacionEnCurso = generarPrimeraImagen(file, nombre);
  return generacionEnCurso;
}

/** Cierra el traspaso (terminó bien o falló) para que un reintento arranque limpio. */
export function limpiarGeneracionPendiente(): void {
  fotoPendiente = null;
  generacionEnCurso = null;
}

export interface ImagenPreparada {
  /** El archivo ya convertido y redimensionado: es el que se sube. */
  file: File;
  /** Object URL para la preview. El caller lo revoca cuando deja de mostrarlo. */
  previewUrl: string;
}

/**
 * Valida y normaliza la foto que eligió el usuario: tipo, tamaño, formatos bloqueados
 * (RAW/TIFF/GIF/SVG, ADR-590), conversión a JPEG de lo que el backend no acepta
 * (HEIC/HEIF, AVIF, WEBP) y reducción a `MAX_LADO_PX`. Lanza `ImagenInvalidaError`
 * con el texto a mostrar.
 */
export async function prepararImagen(file: File): Promise<ImagenPreparada> {
  if (!file.type.startsWith('image/')) {
    throw new ImagenInvalidaError('Por favor seleccioná un archivo de imagen válido');
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new ImagenInvalidaError(
      'El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.',
    );
  }
  // `normalizeImageForUpload` es la política ÚNICA de formatos de la app: rechaza los
  // bloqueados (RAW/TIFF/GIF/SVG) y convierte a JPEG lo que el backend no acepta
  // —HEIC/HEIF, **AVIF** y WEBP—. El código heredado de `AddFirstProduct` gateaba a
  // mano con `isHeicFile || isWebpFile`, así que **un AVIF se subía sin convertir**
  // sólo en este flujo (ADR-2026-08-03-onboarding-usa-la-politica-unica-de-formatos).
  let procesado: File;
  try {
    procesado = await normalizeImageForUpload(file);
  } catch (err) {
    // `err.message` y no la constante: la clase también cubre "el navegador no pudo
    // leer el archivo" (ADR-2026-10-24-subida-lee-el-archivo-antes), con su propio texto.
    throw new ImagenInvalidaError(
      err instanceof UnsupportedImageFormatError
        ? err.message || UNSUPPORTED_FORMAT_MESSAGE
        : 'No pudimos convertir esa imagen. Probá con una en formato JPG o PNG.',
    );
  }

  procesado = await resizeImageIfNeeded(procesado, MAX_LADO_PX, 0.85);
  return { file: procesado, previewUrl: URL.createObjectURL(procesado) };
}

export interface PrimeraGeneracionResult {
  imageIds: Array<number | string>;
  companyId: string;
  /** Payload usado, para que la pantalla de resultados pueda ofrecer "generar más". */
  requestData: Record<string, unknown>;
}

/**
 * Crea el producto con la foto subida y dispara la generación REAL.
 *
 * Secuencia (idéntica a la de `AddFirstProduct`, que es la que el backend espera):
 * marca default → producto → variante → subida (full + thumbnail) → fila de imagen →
 * categorización (fire & forget) → generación.
 *
 * Consume créditos de la empresa. Devuelve los ids de las imágenes generadas; los
 * errores de generación se propagan como `GenerateAdError` (los accionables traen
 * mensaje para el usuario) o `Error` genérico.
 */
export async function generarPrimeraImagen(
  file: File,
  nombreProducto: string,
): Promise<PrimeraGeneracionResult> {
  const nombreFinal = nombreProducto.trim() || 'Producto 1';

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('No se encontró el usuario');

  const { data: profile } = await supabase
    .from('profiles')
    .select('id_company')
    .eq('id', user.id)
    .single();
  const idCompany = (profile as { id_company?: string } | null)?.id_company;
  if (!idCompany) throw new Error('No se encontró la empresa del usuario');

  // Rescate de una tanda anterior EN VUELO: si el intento previo cortó por timeout
  // del poll pero el backend siguió generando (el marcador se conserva en esos
  // fallos), acá se chequea con UN query si esas imágenes ya aterrizaron. Si sí,
  // se devuelven ÉSAS —ya cobradas— en vez de disparar (y cobrar) otra tanda.
  // La foto nueva del usuario se ignora a propósito en ese caso: mostrar el
  // resultado ya pagado gana sobre repetir el gasto.
  const marcaPrevia = leerMarcaPendiente();
  if (marcaPrevia && marcaPrevia.companyId === idCompany) {
    const { data: filasPrevias } = await supabase
      .from('imagenes_generadas')
      .select('id, has_error')
      .eq('execution_id', marcaPrevia.executionId)
      .eq('id_company', idCompany)
      .or('active.eq.true,has_error.eq.true');
    const okPrevias = (filasPrevias ?? []).filter(
      (r) => !(r as { has_error: boolean }).has_error,
    );
    // Sin filas todavía ≠ job muerto: se le pregunta el ESTADO al backend
    // (POST /v1/ads/executions/status). queued/running → la tanda sigue viva y
    // hay que re-engancharla, no pagar otra. failed/not_found/error → recién
    // ahí se presume muerta y el intento nuevo la reemplaza.
    let tandaViva = okPrevias.length > 0;
    if (!tandaViva && USE_ATLAS_BACKEND) {
      try {
        const res = await atlasBackendFetch('/v1/ads/executions/status', {
          execution_id: marcaPrevia.executionId,
        });
        const info = (await res.json()) as { status?: string };
        tandaViva = info.status === 'queued' || info.status === 'running';
      } catch {
        // Backend inaccesible: no se puede confirmar nada — se sigue con el
        // intento nuevo (comportamiento previo).
      }
    }
    if (tandaViva) {
      // Se re-engancha el poll hasta completar la tanda (o su corte por
      // estabilidad/timeout) y se devuelve — costo cero, ya está pagada.
      const rows = await pollGeneratedByExecutionId({
        executionId: marcaPrevia.executionId,
        companyId: idCompany,
        expectedCount: CANTIDAD_IMAGENES,
      });
      const ok = rows.filter((r) => !r.has_error);
      if (ok.length > 0) {
        limpiarMarcaPendiente();
        recursosPrevios = null;
        return {
          imageIds: ok.map((r) => r.id),
          companyId: idCompany,
          requestData: buildRequestData(user.id, marcaPrevia.variantId),
        };
      }
      // Poll agotado con el job "vivo" que nunca escribió: caso patológico
      // (job zombi) — se sigue con el intento nuevo, que pisa el marcador.
    }
  }

  // La marca default la crea el alta, pero es best-effort: si falló en silencio, el
  // producto quedaría huérfano. `ensureDefaultBrand` es idempotente, así que esta
  // llamada cierra la carrera.
  await ensureDefaultBrand(idCompany);

  const { data: defaultBrand } = await supabase
    .from('brand_profiles')
    .select('id')
    .eq('id_company', idCompany)
    .eq('is_default', true)
    .eq('is_draft', false)
    .limit(1)
    .maybeSingle();
  const brandId = (defaultBrand as { id?: string } | null)?.id;

  // Reuso en reintento: si un intento anterior falló DESPUÉS de crear el
  // producto/variante, se reusan (actualizando el nombre si cambió) en vez de
  // crear otros — sin esto cada retry dejaba un producto huérfano.
  // Cuenta distinta (logout/login sin recargar la SPA) → los recursos previos
  // son de otra empresa: se descartan, no se cruzan tenants.
  if (recursosPrevios && recursosPrevios.idCompany !== idCompany) {
    recursosPrevios = null;
  }
  let productId: string;
  let variantId: string;
  if (recursosPrevios) {
    productId = recursosPrevios.productId;
    if (recursosPrevios.nombre !== nombreFinal) {
      await supabase.from('productos').update({ nombre: nombreFinal }).eq('id', productId);
      if (recursosPrevios.variantId) {
        await supabase
          .from('variantes')
          .update({ nombre: nombreFinal })
          .eq('id', recursosPrevios.variantId);
      }
      recursosPrevios.nombre = nombreFinal;
    }
    if (recursosPrevios.variantId) {
      variantId = recursosPrevios.variantId;
    } else {
      // El intento anterior murió entre los dos inserts: se completa la
      // variante faltante sobre el MISMO producto, sin crear otro.
      const { data: variant, error: variantError } = await supabase
        .from('variantes')
        .insert({ id_company: idCompany, nombre: nombreFinal, activo: true, product_id: productId })
        .select('id')
        .single();
      if (variantError) throw variantError;
      variantId = (variant as { id: string }).id;
      recursosPrevios.variantId = variantId;
    }
  } else {
    const { data: producto, error: productoError } = await supabase
      .from('productos')
      .insert({
        id_company: idCompany,
        nombre: nombreFinal,
        tiene_variantes: false,
        ...(brandId ? { brand_id: brandId } : {}),
      })
      .select('id')
      .single();
    if (productoError) throw productoError;
    productId = String((producto as { id: string | number }).id);

    // Registrado APENAS existe el producto (variante pendiente): si el insert
    // de la variante falla, el retry la completa en vez de crear otro producto
    // huérfano.
    recursosPrevios = {
      idCompany,
      productId,
      variantId: null,
      nombre: nombreFinal,
      imagenInsertada: false,
    };

    const { data: variant, error: variantError } = await supabase
      .from('variantes')
      .insert({ id_company: idCompany, nombre: nombreFinal, activo: true, product_id: productId })
      .select('id')
      .single();
    if (variantError) throw variantError;
    variantId = (variant as { id: string }).id;
    recursosPrevios.variantId = variantId;
  }

  // Nombre saneado + timestamp: el bucket no acepta cualquier caracter y dos subidas
  // del mismo archivo no se pisan.
  const fileBaseName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;

  const { error: fullErr } = await supabase.storage
    .from('productos')
    .upload(`${idCompany}/${variantId}/full/${fileBaseName}`, file);
  if (fullErr) throw fullErr;

  // Thumbnail: si falla, se sigue igual (la app cae a la imagen full).
  try {
    const blob = await buildThumbnailBlob(file);
    if (blob) {
      await supabase.storage
        .from('productos')
        .upload(`${idCompany}/${variantId}/opt/${fileBaseName}`, blob);
    }
  } catch (err) {
    console.warn('[primeraGeneracion] no se pudo crear el thumbnail, se sigue', err);
  }

  if (recursosPrevios?.imagenInsertada) {
    // Reintento con la fila ya creada: se apunta al archivo nuevo en vez de
    // insertar una segunda "principal".
    const { error: imageError } = await supabase
      .from('productos_imagenes')
      .update({ url: fileBaseName })
      .eq('variante_id', variantId)
      .eq('principal', true);
    if (imageError) throw imageError;
  } else {
    const { error: imageError } = await supabase
      .from('productos_imagenes')
      .insert({
        id_company: idCompany,
        variante_id: variantId,
        url: fileBaseName,
        principal: true,
        orden: 1,
      });
    if (imageError) throw imageError;
    if (recursosPrevios) recursosPrevios.imagenInsertada = true;
  }

  // Categorización IA: fire & forget. Este flujo NO manda `modo` — contrato con el
  // backend, mantener así.
  categorizeProduct(productId, { userId: user.id });

  const requestData = buildRequestData(user.id, variantId);

  if (USE_ATLAS_BACKEND) {
    // Motor V3 (`/v1/ads/generate`). `execution_id` va APARTE de `requestData`
    // porque ese payload se reusa en "generar más" y no debe arrastrar el mismo id.
    // Si el fetch devuelve 202 o se corta, el poll por execution_id resuelve igual.
    const executionId = crypto.randomUUID();
    // Marcador persistente ANTES de disparar: si el usuario refresca mientras el
    // backend genera, `recuperarGeneracionPendiente` re-engancha esta tanda en vez
    // de rebotar al paso 4 (donde un reintento cobraría créditos de nuevo).
    marcarPendiente({ executionId, companyId: idCompany, variantId });
    try {
      const result = await awaitImageGeneration({
        fetchPromise: postGenerateAdAtlas({
          ...requestData,
          execution_id: executionId,
          modo_utilizado: 'primera_imagen',
        }),
        executionId,
        companyId: idCompany,
        expectedCount: CANTIDAD_IMAGENES,
      });
      recursosPrevios = null; // éxito: no hay nada que reusar en un próximo intento
      limpiarMarcaPendiente();
      return {
        imageIds: result.imageIds,
        companyId: result.companyId || idCompany,
        requestData,
      };
    } catch (err) {
      // Limpieza CONDICIONAL del marcador: en error accionable (sin créditos /
      // rechazo del modelo) el backend no va a producir imágenes → afuera. Pero si
      // el poll agotó su tope, el job puede seguir corriendo: el marcador se
      // CONSERVA (TTL 10 min) para que un refresh o el rescate del próximo intento
      // re-enganchen esa tanda en vez de pagar una segunda.
      if (err instanceof GenerateAdError && err.isActionable) {
        limpiarMarcaPendiente();
      }
      throw err;
    }
  }

  const data = (await postGenerateAd(requestData)) as {
    imagenes_generadas_ids?: Array<number | string>;
    id_company?: string;
  };
  if (!Array.isArray(data?.imagenes_generadas_ids) || data.imagenes_generadas_ids.length === 0) {
    throw new Error('No se recibieron imágenes generadas.');
  }
  recursosPrevios = null;
  return {
    imageIds: data.imagenes_generadas_ids,
    companyId: data.id_company || idCompany,
    requestData,
  };
}

/** Payload de la generación — compartido entre el disparo normal y la
 *  reconstrucción del resultado al recuperar tras un refresh. */
function buildRequestData(userId: string, variantId: string): Record<string, unknown> {
  return {
    user_id: userId,
    variante_id: [variantId],
    mode: 'single',
    modo: 'rapido',
    estilo: 'automatico',
    tipo: 'imagen',
    cantidad_imagenes: CANTIDAD_IMAGENES,
    Format: '4:5',
    composicion: 'modelo',
    modo_pro: true,
    new_ui: true,
  };
}

/**
 * Re-engancha una generación que quedó EN VUELO cuando la página se refrescó (el
 * estado de módulo muere, pero el marcador de localStorage sobrevive). Devuelve una
 * promesa que poolea la tanda por `execution_id` hasta encontrar las imágenes, o
 * `null` si no hay nada pendiente (marcador ausente o más viejo que 10 min).
 *
 * Idempotente igual que `generacionPendiente`: N llamadas (StrictMode) → un poll.
 */
export function recuperarGeneracionPendiente(): Promise<PrimeraGeneracionResult> | null {
  if (generacionEnCurso) return generacionEnCurso;
  const marca = leerMarcaPendiente();
  if (!marca) return null;
  const promesa = (async (): Promise<PrimeraGeneracionResult> => {
    // Dueño primero: si cambió la cuenta desde que se dejó el marcador
    // (logout/login en el mismo browser), la tanda es de OTRA empresa — su
    // RLS jamás la va a mostrar y el poll colgaría 5 min al pedo. Se corta ya,
    // pero SIN borrar el marcador: es de la otra cuenta, y si su dueño vuelve
    // dentro del TTL todavía puede recuperar su tanda pagada (el TTL de 10 min
    // lo limpia solo si nadie lo reclama).
    const {
      data: { user: userActual },
    } = await supabase.auth.getUser();
    const { data: perfilActual } = await supabase
      .from('profiles')
      .select('id_company')
      .eq('id', userActual?.id ?? '')
      .maybeSingle();
    const companyActual = (perfilActual as { id_company?: string } | null)?.id_company;
    if (!companyActual || companyActual !== marca.companyId) {
      throw new Error('No encontramos una generación pendiente. Probá de nuevo.');
    }
    const rows = await pollGeneratedByExecutionId({
      executionId: marca.executionId,
      companyId: marca.companyId,
      expectedCount: CANTIDAD_IMAGENES,
    });
    const ok = rows.filter((r) => !r.has_error);
    if (ok.length === 0) {
      // Poll seco NO prueba que el job haya muerto: el marcador se CONSERVA
      // (con su TTL de 10 min) para que el rescate del próximo intento pueda
      // re-enganchar la tanda si aterriza tarde — mismo criterio que el disparo.
      // Borrarlo acá reabría el doble cobro que este mecanismo existe para evitar.
      throw new Error('No se generó ninguna imagen. Probá de nuevo.');
    }
    // La tanda recuperada ES la buena: sus recursos ya no deben reusarse y el
    // marcador se cierra recién acá, con el resultado en la mano.
    recursosPrevios = null;
    limpiarMarcaPendiente();
    return {
      imageIds: ok.map((r) => r.id),
      companyId: marca.companyId,
      requestData: buildRequestData(userActual?.id ?? '', marca.variantId),
    };
  })();
  generacionEnCurso = promesa;
  return promesa;
}
